/// <amd-module name="Repos/Views/FilesHubActions/Components/ForkPanel" />
import * as React from "react";

import * as Resources from "Repos/Views/FilesHubActions/Resources";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { ProjectsDropdown } from "Tfs/ProjectsDropdown/Components/ProjectsDropdown";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";

export interface IForkDetails {
    repositoryName: string;
    project: TeamProjectReference;
    includeAllBranches: boolean;
}

export interface IForkPanelProps {
    defaultBranchName: string;
    errorMessage: IReadonlyObservableValue<string>;
    initialProject: TeamProjectReference;
    initialRepositoryName: string;
    onDismiss: (details?: IForkDetails) => void;
    showOverlay: IReadonlyObservableValue<boolean>;
    userToken: string;
}

export class ForkPanel extends React.Component<IForkPanelProps> {
    private repositoryName: ObservableValue<string>;
    private repositoryNameMessageIsError = new ObservableValue(false);
    private repositoryNameMessage = new ObservableValue("");
    private branchOptionId = new ObservableValue("default-branch");
    private fieldsValid = new ObservableValue<boolean>(true);
    private selectedProject: TeamProjectReference;

    constructor(props: IForkPanelProps) {
        super(props);

        this.repositoryName = new ObservableValue(props.initialRepositoryName + "." + props.userToken);
        this.selectedProject = props.initialProject;
    }

    public render() {
        return (
            <Observer valid={this.fieldsValid} showOverlay={this.props.showOverlay}>
                {(props: { valid: boolean; showOverlay: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => {
                                onDismiss();
                            },
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: Resources.Fork
                        }
                    ];
                    return (
                        <Panel
                            defaultActiveElement={".repos-fork-panel-repository-name"}
                            onDismiss={() => {
                                onDismiss();
                            }}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: format(Resources.ForkFormat, this.props.initialRepositoryName) }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <Observer errorMessage={this.props.errorMessage}>
                                    {(props: { errorMessage: string }) => {
                                        return (
                                            props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                        );
                                    }}
                                </Observer>
                                <FormItem
                                    label={Resources.RepositoryName}
                                    error={this.repositoryNameMessageIsError}
                                    message={this.repositoryNameMessage}
                                >
                                    <TextField
                                        onChange={this.onRepositoryNameChanged}
                                        onKeyDown={this.onKeyDown}
                                        value={this.repositoryName}
                                        inputClassName="repos-fork-panel-repository-name"
                                    />
                                </FormItem>
                                <FormItem label={Resources.Project}>
                                    <ProjectsDropdown
                                        initialSelection={this.selectedProject}
                                        onSelectedProjectChanged={this.onSelectedProjectChanged}
                                    />
                                </FormItem>
                                <RadioButtonGroup
                                    onSelect={value => (this.branchOptionId.value = value)}
                                    selectedButtonId={this.branchOptionId}
                                    text={Resources.BranchesToIncludeLabel}
                                >
                                    <RadioButton
                                        id={"default-branch"}
                                        text={format(Resources.BranchesToIncludeDefaultFormat, this.props.defaultBranchName)}
                                    />
                                    <RadioButton id={"all-branches"} text={Resources.BranchesToIncludeAll} />
                                </RadioButtonGroup>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        this.props.onDismiss({
            repositoryName: this.repositoryName.value,
            project: this.selectedProject,
            includeAllBranches: this.branchOptionId.value === "all-branches"
        });
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter && this.fieldsValid.value) {
            this.submit();
        }
    };

    private onRepositoryNameChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.repositoryName.value = value;

        if (!value) {
            this.repositoryNameMessage.value = Resources.RepositoryNameRequired;
            this.repositoryNameMessageIsError.value = true;
        } else {
            this.repositoryNameMessage.value = "";
            this.repositoryNameMessageIsError.value = false;
        }

        const fieldsValid = !this.repositoryNameMessageIsError.value;
        if (this.fieldsValid.value !== fieldsValid) {
            this.fieldsValid.value = fieldsValid;
        }
    };

    private onSelectedProjectChanged = (event: React.SyntheticEvent<HTMLElement>, project: TeamProjectReference): void => {
        this.selectedProject = project;
    };
}
